import React, { useEffect } from 'react';
import { IGetModifierGroupsTemplateInfo, UserRoles } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { preventDefaultForEnterKey } from 'utils/helpers';
import { Form } from 'antd';
import { DraggableModifierGroup } from './DraggableModifierGroup';
import style from './DnDList.module.scss';

interface IDnDList {
	setModifierGroups: (value: string[]) => void;
	setModifierGroupsWithAdditionalData: (value: IGetModifierGroupsTemplateInfo['templates']) => void;
	changeDownLimit: (id: string, value: Nullable<number>) => void;
	changeUpLimit: (id: string, value: Nullable<number>) => void;
	toggleIsLimitsProtected: (id: string, value: boolean) => void;
	toggleIsAdditional: (id: string, value: boolean) => void;
	modifierGroups: string[];
	modifierGroupsWithAdditionalData: IGetModifierGroupsTemplateInfo['templates'];
}

export const DnDList: React.FC<IDnDList> = React.memo(
	({
		setModifierGroups,
		setModifierGroupsWithAdditionalData,
		changeDownLimit,
		changeUpLimit,
		toggleIsLimitsProtected,
		toggleIsAdditional,
		modifierGroups,
		modifierGroupsWithAdditionalData,
	}) => {
		const [form] = Form.useForm();
		const hasChangePositionAccess = useRoleAccess([
			UserRoles.SUPER_USER,
			UserRoles.CLIENT_MANAGER,
			UserRoles.GROUP_MANAGER,
			UserRoles.TECHNICAL,
		]);

		const handleDragEnd = (result: DropResult) => {
			if (!modifierGroupsWithAdditionalData.length || !result.destination) {
				return null;
			}
			const items = modifierGroupsWithAdditionalData;
			const [reorderedItem] = items.splice(result.source.index, 1);
			items.splice(result.destination.index, 0, reorderedItem);
			setModifierGroups(items.map((el) => el.id));
		};

		const handleRemove = (id: string) => {
			const updatedModifierGroups = modifierGroups.filter((el) => el !== id);
			const updatedModifierGroupsWithAdditionalData = modifierGroupsWithAdditionalData.filter(
				(modifierGroupWithAdditionalData) => modifierGroupWithAdditionalData.id !== id,
			);
			setModifierGroups(updatedModifierGroups);
			setModifierGroupsWithAdditionalData(updatedModifierGroupsWithAdditionalData);
		};

		useEffect(() => {
			modifierGroupsWithAdditionalData.forEach((el) => {
				form.setFieldsValue({
					[`downLimit-${el.id}`]: el.downLimit,
					[`upLimit-${el.id}`]: el.upLimit,
					[`isLimitsProtected-${el.id}`]: el.isLimitsProtected,
					[`isAdditional-${el.id}`]: el.isAdditional,
				});
			});
		}, [modifierGroupsWithAdditionalData]);

		return (
			<Form form={form} name="basic" onKeyPress={preventDefaultForEnterKey} layout="vertical">
				<DragDropContext onDragEnd={handleDragEnd}>
					<Droppable droppableId="modifierGroupList">
						{(provided) => (
							<div className={style.groupsList} ref={provided.innerRef} {...provided.droppableProps}>
								{modifierGroupsWithAdditionalData.map((el, index) => (
									<React.Fragment key={el.id}>
										<Draggable draggableId={el.id} index={index}>
											{(provided) => (
												<DraggableModifierGroup
													draggableInnerRef={provided.innerRef}
													changeDownLimit={changeDownLimit}
													changeUpLimit={changeUpLimit}
													toggleIsLimitsProtected={toggleIsLimitsProtected}
													toggleIsAdditional={toggleIsAdditional}
													onDelete={handleRemove}
													provided={provided}
													positionNumber={index + 1}
													label={el.name || '-'}
													modifierGroupId={el.id}
													isLimitsProtected={el.isLimitsProtected}
													isAdditional={el.isAdditional}
													hasDragAccess={hasChangePositionAccess}
												/>
											)}
										</Draggable>
									</React.Fragment>
								))}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			</Form>
		);
	},
);
